import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, Calendar, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { media } from "@/lib/api";
import { useActiveClient } from "@/hooks/use-active-client";

export const Route = createFileRoute("/_auth/media/campaigns")({
  component: MediaCampaigns,
});

function MediaCampaigns() {
  const navigate = useNavigate();
  const { activeClientId } = useActiveClient();
  const { data, isLoading } = useQuery({
    queryKey: ["media", "campaigns", activeClientId],
    queryFn: () => media.listCampaigns({ limit: 200, client_id: activeClientId ?? undefined }),
  });
  const clientsQ = useQuery({
    queryKey: ["media", "clients", "active"],
    queryFn: () => media.listClients({ is_active: true, limit: 200 }),
  });

  // Con "Todos los clientes" cada campaña muestra a qué cliente pertenece.
  const clientTag = (clientId: number): string | null => {
    if (activeClientId != null) return null;
    return (clientsQ.data ?? []).find(c => c.id === clientId)?.name ?? `Cliente #${clientId}`;
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-ink-500">{(data ?? []).length} campañas</div>
        <Button onClick={() => navigate({ to: "/media/campaigns/new" })}>
          <Plus size={14} /> Nueva campaña
        </Button>
      </div>

      {isLoading ? <Skeleton className="h-40" /> : (
        <div className="space-y-3">
          {(data ?? []).map(c => (
            <Link key={c.id} to="/media/campaigns/$campaignId" params={{ campaignId: String(c.id) }} className="block">
              <Card className="hover:shadow-md transition-shadow hover:border-brand-300">
                <CardContent className="py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold truncate">{c.name}</span>
                      {clientTag(c.client_id) && (
                        <Badge variant="outline" className="text-[10px] text-brand-600 border-brand-300 shrink-0">
                          {clientTag(c.client_id)}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-ink-500 mt-1 flex items-center gap-2">
                      {c.period_start && c.period_end && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={12} />
                          {c.period_start} → {c.period_end}
                        </span>
                      )}
                      {c.budget_amount && (
                        <span>· {c.currency} {Number(c.budget_amount).toLocaleString("es-PE")}</span>
                      )}
                    </div>
                  </div>
                  <Badge variant={c.status === "activa" ? "default" : "outline"}>{c.status}</Badge>
                  <ChevronRight size={16} className="text-ink-400 shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
          {!(data ?? []).length && (
            <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              No hay campañas. Crea la primera con el wizard.
            </div>
          )}
        </div>
      )}
    </>
  );
}
